/**
 * 브리핑 편성 정책 — 센터마다 몇 건을 보낼지 결정한다.
 *
 * 5건 고정은 틀렸다. 회원 30명 센터와 500명 센터가 같을 이유가 없고,
 * 조용한 날에 억지로 채우면 신뢰를 잃는다.
 *
 * 규칙
 *   1) 위험(DANGER)은 개수 제한 없이 전부 올린다. 돈이 걸려 있다.
 *   2) 나머지는 센터 규모에 비례한 상한 안에서 채운다.
 *   3) 상한에 못 미쳐도 억지로 채우지 않는다.
 *   4) 긍정 항목(기회·칭찬)은 최소 1건 보장한다. 나쁜 소식만 오면 안 열린다.
 */

import { BriefingCompositionDto } from '../../types/opsBriefing';

export type CenterSize = 'SMALL' | 'MEDIUM' | 'LARGE';

/**
 * 활성 회원 수 → 센터 규모. 라포 전체 중앙값이 80명이다.
 *
 * ⚠️ 활성 회원 수를 모르면(null) 규모도 모른다 — null 을 돌려준다.
 * 예전에는 호출부에서 `activeMembers ?? 0` 으로 막았는데, 그 0 이 그대로
 * 'SMALL' 이 되어 "활성 0명 센터 기준 하루 최대 3건"이라는 없는 사실을 만들었다.
 * 모르는 것은 모른다고 해야 한다.
 */
export const resolveCenterSize = (activeMembers: number | null): CenterSize | null => {
    if (activeMembers == null) return null;
    if (activeMembers <= 40) return 'SMALL';
    if (activeMembers <= 150) return 'MEDIUM';
    return 'LARGE';
};

export type BriefingQuota = {
    size: CenterSize;
    label: string;
    /** 권장 총 건수 상한 */
    maxItems: number;
    /** 긍정 항목 최소 보장 */
    minPositive: number;
    description: string;
};

export const QUOTA: Record<CenterSize, BriefingQuota> = {
    SMALL: {
        size: 'SMALL',
        label: '소형',
        maxItems: 3,
        minPositive: 1,
        description: '회원 40명 이하. 매일 3건이 넘으면 과잉입니다',
    },
    MEDIUM: {
        size: 'MEDIUM',
        label: '중형',
        maxItems: 5,
        minPositive: 1,
        description: '회원 41~150명. 하루 5건이 읽기 좋은 분량입니다',
    },
    LARGE: {
        size: 'LARGE',
        label: '대형',
        maxItems: 8,
        minPositive: 2,
        description: '회원 151명 이상. 위임 대상이 많아 8건까지 다룹니다',
    },
};

/**
 * 실제 편성 결과 설명 — 화면에 "왜 이 개수인지"를 밝힌다.
 * 대표님이 "오늘은 왜 2건뿐이지?"라고 의심하면 신뢰가 떨어진다.
 *
 * ─────────────────────────────────────────────────────────────
 * ⚠️ 총 건수는 규모 상한을 **넘을 수 있다**.
 * ─────────────────────────────────────────────────────────────
 * 서버 편성에 긍정 항목 최소 보장(minPositive)이 들어가면서, 위험이 상한을
 * 다 먹은 날에는 기회 자리를 **더 열어서** 넣는다(위험을 밀어내지 않는다).
 * 그래서 `총량 = max(규모 상한, 위험 + 긍정 보장)` 이 되고,
 * `위험 ≤ 상한` 인데도 총량이 상한을 넘는 날이 생긴다.
 * 예: 중형(상한 5)에서 위험 5 + 기회 1 = 6건.
 *
 * 그런 날 "기준 하루 최대 5건"이라 적으면서 6장을 그리면 화면이 거짓말을 한다.
 * 보장이 발동한 날에는 상한을 단정하지 않는다.
 *
 * 상한 값(maxItems)은 **서버가 그날 실제로 적용한 값**(composition)을 쓴다.
 * 프론트 상수로 설명하면 두 레포의 숫자가 갈리는 순간 화면이 조용히 거짓말을 한다 —
 * "같기로 약속했다"와 "그날 실제로 적용됐다"는 다른 사실이다.
 * 편성 기록이 없으면(옛 문서·미생성 = null) 예전과 똑같이 프론트 상수로 설명한다.
 */
type QuotaInput = {
    size: CenterSize | null;
    total: number;
    danger: number;
    activeMembers: number | null;
    /**
     * 서버 편성 기록. **null 은 "모른다"** 이고, 그때는 예전 문구가 그대로 나간다.
     * (0 으로 채운 값을 넘기면 "상한 0건 센터"라는 없는 사실이 생긴다)
     */
    composition?: BriefingCompositionDto | null;
};

/**
 * 가리기 때문에 빠진 사람 — 편성 설명 뒤에 붙는 한 줄.
 *
 * 🔴 백엔드 v9 부터 회원 가리기가 **브리핑 생성에서도 제외**된다 (2026-08-31).
 *    그 전에는 회원 현황에서만 내려갔다. 이제는 「왜 오늘 항목이 적은가」의
 *    답 중 하나가 가리기라서, 편성 설명이 그걸 말하지 않으면 원장은
 *    자기가 가려 놓고 「오늘은 조용하네」로 읽는다.
 *
 * ⚠️ **키가 없으면 한 줄 통째로 생략한다.** 구서버는 이 값을 안 보낸다.
 *    없는 것을 0 으로 읽으면 「제외된 사람이 없었다」는 없는 사실이 생긴다.
 */
const hiddenNote = (comp?: BriefingCompositionDto | null): string => {
    if (comp == null) return '';
    /*
     * 조회 실패가 먼저다. 이 날은 가리기가 **반영되지 않은** 날이라,
     * 「N명 제외」를 적으면 정반대의 사실을 말하게 된다.
     * (실패해도 생성 자체는 성공이므로 브리핑은 정상이다)
     */
    if (comp.hiddenLookupFailed) return '가림 목록을 확인하지 못해 이번 생성엔 반영하지 못했습니다';
    const n = comp.hiddenExcluded ?? 0;
    return n > 0 ? `가린 회원 ${n}명 제외` : '';
};

/**
 * 편성 설명 한 줄과 **그 문장의 성격**.
 *
 * ─────────────────────────────────────────────────────────────
 * 왜 성격을 함께 돌려주나 (2026-09-01 QA32)
 * ─────────────────────────────────────────────────────────────
 * 이 함수의 갈래 중 하나는 상한 이야기를 아예 하지 않고 **편성만** 말합니다 —
 * 「위험 N건은 상한과 무관하게 모두 브리핑에 올렸습니다」. 대표 QA 에서
 * 오늘의 브리핑은 그런 편성 설명을 화면에서 뺐습니다(설명글이 너무 많다).
 *
 * 그렇다고 화면이 문자열을 보고 판단하게 두면 안 됩니다 — 문구를 한 글자만
 * 고쳐도 조용히 다시 뜹니다. 그래서 **문장을 만드는 자리가 성격까지** 말합니다.
 *
 * ⚠️ 문장 자체는 지우지 않았습니다. 지난 리포트·핸드오프가 같은 셈을 쓰고,
 *    「그날 무엇이 왜 올라갔나」는 여전히 사실입니다. 오늘의 브리핑 화면만
 *    그 한 갈래를 안 그립니다.
 */
export type QuotaNote = {
    /** 화면에 그대로 적을 한 줄 — 가리기 안내까지 붙은 값 */
    text: string;
    /** 상한이 아니라 **편성**만 말하는 문장인가 */
    compositionOnly: boolean;
    /**
     * 가리기 안내만 따로.
     *
     * 🔴 편성 설명을 빼도 **이 사실은 남아야 합니다.** 안 밝히면 원장님은
     *    자기가 가려 놓고 「오늘은 조용하네」로 읽습니다.
     */
    hiddenNote: string;
};

/**
 * 문장과 성격을 함께 만든다.
 *
 * ⚠️ 성격을 **문자열로 되짚지 마세요.** 「위험 …」으로 시작하는 문장이 둘이라
 *    (상한 예외 · 하루 절대 상한에 걸린 날) 접두사로 가르면 엉뚱한 갈래까지
 *    화면에서 사라집니다. 갈래마다 자기 성격을 직접 적습니다.
 */
type QuotaBase = { text: string; compositionOnly: boolean };
const plain = (text: string): QuotaBase => ({ text, compositionOnly: false });

const explainQuotaBase = (input: QuotaInput): QuotaBase => {
    const comp = input.composition ?? null;

    if (input.total === 0) return plain('오늘은 조치가 필요한 항목이 없습니다');
    /*
     * 활성 회원 수를 모르면 편성 상한도 모른다.
     * 여기서 아무 규모나 골라 쓰면 "왜 이 개수인지"를 거짓으로 설명하게 된다.
     */
    if (input.size == null)
        return plain(`활성 회원 수 집계 전 · 오늘 올라온 항목 ${input.total}건`);

    /* 규모도 서버가 편성 시점에 굳혀 둔 값이 있으면 그것을 쓴다 */
    const q = QUOTA[comp?.centerSize ?? input.size];
    /*
     * 편성 기록의 상한이 0 이하면 그건 값이 아니라 공백이다.
     * "하루 최대 0건"이라는 없는 사실을 만들지 말고 규모로 아는 값을 쓴다.
     */
    const maxItems = comp && comp.quotaMaxItems > 0 ? comp.quotaMaxItems : q.maxItems;

    /*
     * 위험이 하루 절대 상한에 걸려 잘린 날.
     * 이 날은 "위험은 모두 표시합니다"도 규모 상한도 단정할 수 없다.
     * (원래부터 거짓이던 문구다 — 서버가 사실을 보내주기 전에는 알 방법이 없었다)
     */
    if (comp?.dangerTruncated)
        return plain(
            comp.dangerCandidates > comp.dangerIncluded
                ? `위험 후보 ${comp.dangerCandidates}건 중 ${comp.dangerIncluded}건만 편성된 날입니다`
                : '위험 항목 일부가 하루 절대 상한에 걸려 빠진 날입니다'
        );

    /*
     * ⚠️ "모두 **표시**합니다" 라고 쓰지 않는다.
     *
     * 이 문장이 말하는 것은 **서버 편성**이다 — 상한과 무관하게 브리핑 문서에
     * 전부 올렸다는 뜻이다. 화면은 그것을 다시 다섯 건으로 접는다
     * (briefingComposition). 그래서 "모두 표시" 는 이제 거짓이고,
     * 「챙길 일 5건 · 외 26건」이라 적힌 머리말과 정면으로 어긋난다.
     *
     * 편성(올렸다)과 표시(세웠다)는 다른 층이다. 문장은 자기 층만 말한다.
     */
    if (input.danger > maxItems)
        return {
            text: `위험 ${input.danger}건은 상한과 무관하게 모두 브리핑에 올렸습니다`,
            /*
             * 🔴 이 문장만 상한 이야기를 아예 안 한다 — 편성 설명이다.
             *    2026-09-01 QA32 에서 오늘의 브리핑이 이 갈래를 안 그리기로 했다.
             */
            compositionOnly: true,
        };

    /*
     * ═══════════════════════════════════════════════════════════
     * 🔴 상한을 **넘긴 날** — 그날 실제 구성을 그대로 적는다
     * ═══════════════════════════════════════════════════════════
     * 총량이 상한을 넘는 경로가 둘이다. 둘 다 위험을 밀어내지 않고 **자리를
     * 더 열어서** 넣기 때문에 생긴다.
     *   · 기회 최소 보장(minPositive)  — 위험이 상한을 다 먹은 날에도 이만큼은 넣는다
     *   · 휴면 예약석(dormantReserved) — 옛 식에서는 휴면이 구조적으로 영원히 0건이었다
     *
     * 그런 날 「하루 최대 N건」이라 적으면서 N+2 장을 그리면 화면이 거짓말을 한다.
     * 그래서 상한을 단정하지 않고 **무엇이 얼마나 더해졌는지**를 적는다.
     *
     * ⚠️ 숫자는 전부 **서버 편성 기록**(composition)에서 온다. 프론트 상수(QUOTA)로
     *    계산하면 두 레포가 갈리는 순간 화면이 조용히 거짓말을 한다 —
     *    「같기로 약속했다」와 「그날 실제로 적용됐다」는 다른 사실이다.
     *    (서버도 이 어긋남을 알고 있다 — opsBriefingPolicy composeCandidates 주석)
     */
    const dormantReserved = comp?.dormantReserved ?? 0;
    const dormantIncluded = comp?.dormantIncluded ?? 0;
    /*
     * 🔴 **주의도 총량에 넣는다** (2026-09-01 백엔드 4차).
     *
     * 안 넣으면 우변이 좌변보다 작아진다 — 소형 센터에서 「위험 2건 + 기회 3건」인
     * 날 화면은 5장을 그려 놓고 설명은 3건이라 말한다. 원장님이 세어 보면 안 맞는다.
     * 키가 없으면(구버전 서버) 0 이라 예전 문구가 그대로 나온다.
     */
    const watchReserved = comp?.watchReserved ?? 0;
    const watchIncluded = comp?.watchIncluded ?? 0;
    const composedTotal =
        comp != null
            ? comp.dangerIncluded + comp.chanceIncluded + dormantIncluded + watchIncluded
            : 0;

    if (comp != null && composedTotal > maxItems) {
        const extras = [
            comp.minPositive > 0 ? `기회 보장 ${comp.minPositive}건` : null,
            dormantReserved > 0 ? `휴면 예약 ${dormantReserved}건` : null,
            watchReserved > 0 ? `주의 예약 ${watchReserved}건` : null,
        ].filter((v): v is string => v != null);
        /*
         * 더해진 것이 하나도 없는데 총량이 상한을 넘었다면 위험 예외뿐이다.
         * (아래 갈래가 그 경우를 맡는다 — 여기서 빈 문장을 만들지 않는다)
         */
        if (extras.length > 0) {
            return plain(
                `${q.label} 센터 기준 상한 ${maxItems}건 · ` +
                    `오늘은 ${composedTotal}건 = 위험 ${comp.dangerIncluded}건 전부 + ${extras.join(' + ')}`
            );
        }
    }

    /*
     * 위험 예외로 그날 총량이 상한을 넘은 날.
     *
     * 원장이 위험 대부분을 처리해 표시 건수가 상한 아래로 내려오면 위 갈래를 빠져나가는데,
     * 그 상태에서 "하루 최대 8건"이라고 적으면 14건이 올라갔던 날을 8건짜리 날로 만든다.
     * 상한은 위험 외 항목에만 걸린다는 사실을 그대로 적는다.
     * (보장이 아닌 이 경로는 항상 dangerIncluded > 상한 인 날이다 — 그렇지 않으면
     *  총량이 상한을 넘을 수 없다)
     */
    if (comp && comp.dangerIncluded + comp.chanceIncluded > maxItems)
        return plain(
            `${q.label} 센터 기준 상한 ${maxItems}건 · 위험 ${comp.dangerIncluded}건은 상한과 무관하게 올라간 날입니다`
        );

    if (input.total < maxItems)
        return plain(
            `${q.label} 센터 기준 하루 최대 ${maxItems}건 · 오늘은 기준을 넘은 항목이 ${input.total}건입니다`
        );
    /*
     * 마지막 안전장치 — 위 갈래에 안 걸렸는데 표시 건수가 상한을 넘는 경우.
     * 편성 기록이 있는 날에만 적용한다(기록이 없는 날은 예전 문구 그대로여야 한다).
     * 상한을 단정하지 않고, 상한과 현재 건수를 나란히 사실로만 적는다.
     */
    /* 여기도 같은 이유로 '표시 중' 이라 쓰지 않는다 — 화면은 다섯 건만 세운다 */
    if (comp && input.total > maxItems)
        return plain(`${q.label} 센터 기준 상한 ${maxItems}건 · 현재 미처리 ${input.total}건입니다`);
    return plain(`${q.label} 센터(활성 ${input.activeMembers}명) 기준 하루 최대 ${maxItems}건`);
};

/**
 * 편성 설명 한 줄.
 *
 * 상한·구성 설명은 `explainQuotaBase` 가 만들고, 여기서 가리기 한 줄을 덧붙인다.
 * 갈래가 많아서(상한 초과·위험 예외·집계 전 …) 각 return 마다 붙이면
 * 한 군데만 빠뜨려도 그 날만 조용히 설명이 달라진다.
 */
export const quotaNoteOf = (input: QuotaInput): QuotaNote => {
    const base = explainQuotaBase(input);
    const hidden = hiddenNote(input.composition);
    return {
        text: hidden ? `${base.text} · ${hidden}` : base.text,
        compositionOnly: base.compositionOnly,
        hiddenNote: hidden,
    };
};

export const explainQuota = (input: QuotaInput) => quotaNoteOf(input).text;

/**
 * 그날 편성에서 **빠진** 항목 수 — "무엇이 빠졌는가" 판단의 단일 기준.
 *
 * 오늘의 브리핑(explainComposition)과 지난 리포트가 같은 사실을 다르게 세면
 * 두 화면이 같은 날을 놓고 다른 말을 한다. 그래서 셈은 여기 한 곳에만 둔다.
 *
 * ⚠️ **null 은 "모른다"** 다. `{ chance: 0, danger: 0 }` 과 다르다.
 *    편성 기록이 없는 날(옛 문서·미생성)을 0 으로 읽으면
 *    "그날은 빠진 게 없었다"는 없는 사실이 생긴다.
 *
 * 위험은 `dangerTruncated` 가 참인 날만 샌다. 후보와 편성 수가 다른 이유를
 * 우리가 아는 것은 "하루 절대 상한에 걸렸다" 하나뿐이고, 그 외의 차이는
 * 이유를 모르므로 "빠졌다"고 단정하지 않는다.
 */
export type DayLeftOut = {
    /** 자리가 없어 못 올라간 재등록 기회 후보 수 */
    chance: number;
    /** 하루 절대 상한에 걸려 빠진 위험 후보 수 */
    danger: number;
    /** 자리가 없어 못 올라간 휴면 후보 수 (구버전 서버면 0) */
    dormant: number;
    /** 자리가 없어 못 올라간 주의 후보 수 (구버전 서버면 0) */
    watch: number;
    total: number;
};

export const leftOutOf = (
    comp: BriefingCompositionDto | null | undefined,
): DayLeftOut | null => {
    if (!comp) return null;
    const chance = Math.max(0, comp.chanceCandidates - comp.chanceIncluded);
    const danger = comp.dangerTruncated
        ? Math.max(0, comp.dangerCandidates - comp.dangerIncluded)
        : 0;
    /*
     * 🔴 휴면·주의도 「빠진 것」에 넣는다 (2026-09-01 백엔드 4차).
     *
     * 이 셈이 「무엇이 빠졌는가」의 **단일 기준**인데 위험·기회만 세고 있었다.
     * 그래서 휴면 후보 30명이 자리에 밀린 날에도 화면은 「빠진 것 없음」이라
     * 말했다. 주의 카드가 생기면 같은 구멍이 하나 더 늘어난다.
     *
     * 이유는 기회와 같다 — **자리가 없었다.** (하루 절대 상한에 걸리는 것은
     * 위험뿐이고, 그건 `dangerTruncated` 가 따로 말한다)
     * 키가 없으면 0 이라 구버전 서버에서는 예전과 똑같이 동작한다.
     */
    const dormant = Math.max(0, (comp.dormantCandidates ?? 0) - (comp.dormantIncluded ?? 0));
    const watch = Math.max(0, (comp.watchCandidates ?? 0) - (comp.watchIncluded ?? 0));
    return { chance, danger, dormant, watch, total: chance + danger + dormant + watch };
};

/**
 * 편성에서 **빠진** 항목을 밝힌다.
 *
 * 화면에 올라온 것만 보여 주면 원장님은 "오늘은 재등록 기회가 없구나"로 읽는다.
 * 실제로는 후보가 있었는데 위험에 밀려 자리를 못 얻은 날이 있고,
 * 그 차이를 모르면 재등록 기회를 통째로 놓친다.
 *
 * 사실만 적는다 — 후보 수와 올라온 수는 서버가 편성 시점에 센 값이고,
 * 빠진 이유는 "자리가 없었다"(기회) 또는 "하루 절대 상한"(위험) 둘뿐이다.
 * 편성 기록이 없으면(null) 아무 말도 하지 않는다. 모르는 것을 지어내지 않는다.
 */
export const explainComposition = (
    comp: BriefingCompositionDto | null | undefined,
    dayWord: string = '오늘',
): string[] => {
    const leftOut = leftOutOf(comp);
    if (!comp || !leftOut) return [];
    const notes: string[] = [];

    const dangerLeftOut = leftOut.danger;
    if (dangerLeftOut > 0)
        notes.push(
            `위험 후보 ${comp.dangerCandidates}건 중 ${comp.dangerIncluded}건만 ${dayWord} 브리핑에 올랐습니다 · 나머지 ${dangerLeftOut}건은 하루 절대 상한에 걸려 빠졌습니다`,
        );

    const chanceLeftOut = leftOut.chance;
    if (chanceLeftOut > 0)
        notes.push(
            comp.chanceIncluded === 0
                ? `재등록 기회 후보 ${comp.chanceCandidates}건은 자리가 없어 ${dayWord} 브리핑에 오르지 못했습니다`
                : `재등록 기회 후보 ${comp.chanceCandidates}건 중 ${comp.chanceIncluded}건만 ${dayWord} 브리핑에 올랐습니다 · 나머지 ${chanceLeftOut}건은 자리가 없어 빠졌습니다`,
        );

    /*
     * 🔴 휴면·주의가 빠진 날도 밝힌다 (2026-09-01 백엔드 4차).
     *
     * `leftOutOf` 에 두 축을 더했으므로 `total` 이 늘어난다. 그런데 설명 문장이
     * 없으면 지난 리포트가 「오르지 못한 후보 12건」이라 적어 놓고 **무엇이
     * 빠졌는지는 한 줄도 안 말하는** 화면이 된다 — 원장님은 셀 수만 있고
     * 무엇을 놓쳤는지 모른다.
     *
     * 위험·기회와 달리 **한 줄로 합친다.** 둘 다 「자리가 없었다」로 이유가 같고,
     * 편성 설명이 네 줄이 되면 아침에 아무도 안 읽는다.
     */
    const deferred = [
        leftOut.dormant > 0 ? `휴면 ${leftOut.dormant}건` : null,
        leftOut.watch > 0 ? `주의 ${leftOut.watch}건` : null,
    ].filter((v): v is string => v != null);
    if (deferred.length > 0)
        notes.push(
            `${deferred.join(' · ')}은 자리가 없어 ${dayWord} 브리핑에 오르지 못했습니다 · 회원 현황에서 볼 수 있습니다`,
        );

    return notes;
};
